import { Gem, Heart, Users, Flame } from "lucide-react";
import { BRAND } from "@/lib/brand";

const VALUES = [
  {
    icon: Gem,
    title: "Sisterhood First",
    body: "Blood couldn't make us closer. Every queen in this house has your back — on the road, in the city, in life.",
  },
  {
    icon: Flame,
    title: "Ride With Purpose",
    body: "We roll for causes bigger than us — charity rides, community support, and Houston's finest women on 2s and 3s.",
  },
  {
    icon: Heart,
    title: "Rooted In Houston, Ridden Nationwide",
    body: "H-town is our heartbeat — and our sisters rep the crown in cities across the U.S. The road may separate us; the crown never does.",
  },
  {
    icon: Users,
    title: "Two Branches, One Crown",
    body: "Whether you ride or roll with us, there's a seat at the table. MC for riders, SC for supporters — one family.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      className="relative py-24 sm:py-32 lg:py-40 bg-[#050505]"
      data-testid="about-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          {/* Left image column */}
          <div className="lg:col-span-5 lg:sticky lg:top-28" data-testid="about-media">
            <div className="relative">
              <div className="absolute -inset-2 border border-brand/30 translate-x-4 translate-y-4" />
              <img
                src={BRAND.images.aboutRiders}
                alt="Woman riding with Crown Jewels"
                className="relative w-full h-[520px] object-cover"
              />
            </div>
            <div className="mt-8 pl-4 border-l-2 border-brand">
              <p className="font-script text-3xl text-brand leading-tight">
                Like precious jewels,
                <br />
                our sisterhood is rare.
              </p>
              <p className="text-xs uppercase tracking-[0.3em] text-white/50 mt-4">
                — Crown Jewels Motto
              </p>
            </div>
          </div>

          {/* Right text + values */}
          <div className="lg:col-span-7 space-y-10">
            <div>
              <div className="text-xs uppercase tracking-[0.35em] text-brand mb-4 flex items-center gap-3">
                <span className="h-px w-10 bg-brand" /> Our Story
              </div>
              <h2
                className="text-4xl sm:text-5xl lg:text-6xl font-serif leading-[1.02] text-white"
                data-testid="about-title"
              >
                More than a club.
                <br />
                <span className="italic text-white/80">A sisterhood with a crown.</span>
              </h2>
            </div>

            <div className="space-y-6 text-lg text-white/75 leading-relaxed max-w-2xl">
              <p data-testid="about-p1">
                Founded in {BRAND.mcEst} on the streets of Houston, Crown Jewels
                Motorcycle Club was built by women who refused to ride in
                anyone's shadow. Houston is our one and only chapter — and our
                sisters rep that same Houston crown from cities across the
                country.
              </p>
              <p data-testid="about-p2">
                In {BRAND.scEst}, we opened our arms wider — launching the Crown
                Jewels Social Club so every woman who believes in the crown has
                a place at the table, whether she rides or not. And through
                <strong className="text-white"> Miss Crown Jewels</strong>, our
                youth mentorship for girls 2–17, we hand the crown down. One
                family. Two branches. Many states. Endless reasons to rise.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 pt-8">
              {VALUES.map((v, i) => {
                const Icon = v.icon;
                return (
                  <div
                    key={v.title}
                    className="group relative p-6 border border-white/10 hover:border-brand/60 transition-all duration-500 bg-[#0a0a0a] hover:bg-[#111]"
                    data-testid={`about-value-${i}`}
                  >
                    <Icon
                      className="w-7 h-7 text-brand mb-5"
                      strokeWidth={1.5}
                    />
                    <h3 className="text-lg font-serif text-white mb-2">
                      {v.title}
                    </h3>
                    <p className="text-sm text-white/60 leading-relaxed">
                      {v.body}
                    </p>
                    <span className="absolute top-0 left-0 h-px w-0 bg-brand group-hover:w-full transition-all duration-700" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
